import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, Mic, MicOff, Volume2, VolumeX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import VoiceOrb from "./VoiceOrb";

const GREETING =
  "Hello darling, I'm Seraphina — your Guardian Angel at Fallen Angels. How may I take care of you tonight?";

export const ChatWidget = () => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ id: "greeting", role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [orbState, setOrbState] = useState("idle");
  const [voiceOn, setVoiceOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const speak = useCallback(
    async (text) => {
      if (!voiceOn) return;
      try {
        setOrbState("speaking");
        const res = await fetch(`${API}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) throw new Error("tts");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.onended = () => setOrbState("idle");
          await audioRef.current.play();
        }
      } catch (e) {
        setOrbState("idle");
      }
    },
    [voiceOn]
  );

  const sendMessage = useCallback(
    async (text) => {
      const content = (text ?? input).trim();
      if (!content || sending) return;
      setInput("");
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
      setSending(true);
      try {
        const res = await fetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: content }),
        });
        if (!res.ok) throw new Error("chat failed");
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
        speak(data.reply);
      } catch (e) {
        toast.error("Seraphina is momentarily away. Please try again.");
      } finally {
        setSending(false);
      }
    },
    [input, sending, sessionId, speak]
  );

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setOrbState("idle");
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "recording.webm");
          const res = await fetch(`${API}/stt`, { method: "POST", body: fd });
          const data = await res.json();
          if (data.text && data.text.trim()) {
            sendMessage(data.text.trim());
          } else {
            toast.error("I didn't quite catch that, love.");
          }
        } catch (e) {
          toast.error("Could not transcribe audio.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setOrbState("listening");
    } catch (e) {
      toast.error("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const toggleMic = () => (recording ? stopRecording() : startRecording());

  return (
    <div
      className="w-full max-w-lg mx-auto bg-[#141414]/80 backdrop-blur-xl border border-[#27272A] rounded-lg overflow-hidden"
      data-testid="chat-widget"
    >
      <audio ref={audioRef} className="hidden" />

      {/* Orb header */}
      <div className="flex flex-col items-center pt-8 pb-4 border-b border-[#27272A] relative">
        <button
          onClick={() => setVoiceOn((v) => !v)}
          className="absolute right-4 top-4 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
          data-testid="voice-toggle-button"
          title={voiceOn ? "Mute voice" : "Unmute voice"}
        >
          {voiceOn ? <Volume2 size={20} /> : <VolumeX size={20} />}
        </button>
        <VoiceOrb state={orbState} size={140} />
        <p className="font-display text-2xl mt-3 text-[#F5F5F5]">Seraphina</p>
        <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mt-1">
          Your Guardian Angel
        </p>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="h-72 overflow-y-auto px-5 py-4 space-y-4"
        data-testid="chat-messages"
      >
        {messages.map((m, i) => (
          <div
            key={m.id || `${m.role}-${i}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] px-4 py-2.5 rounded-lg text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#5C1A24] text-[#F5F5F5]"
                  : "bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F5]"
              }`}
              data-testid={`chat-message-${m.role}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {(sending || transcribing) && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#27272A] text-[#A3A3A3] flex items-center gap-2 text-sm">
              <Loader2 size={14} className="animate-spin" />
              {transcribing ? "Listening…" : "Seraphina is typing…"}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 p-3 border-t border-[#27272A] bg-[#0A0A0A]/60">
        <button
          onClick={toggleMic}
          className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
            recording
              ? "bg-[#5C1A24] text-white animate-pulse"
              : "bg-[#141414] text-[#D4AF37] hover:bg-[#1e1e1e] border border-[#27272A]"
          }`}
          data-testid="mic-button"
          title={recording ? "Stop & send" : "Speak to Seraphina"}
        >
          {recording ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Whisper your request…"
          className="flex-1 bg-transparent outline-none text-[#F5F5F5] placeholder:text-[#6b6b6b] text-sm px-2"
          data-testid="chat-input"
        />
        <button
          onClick={() => sendMessage()}
          disabled={sending || !input.trim()}
          className="shrink-0 w-11 h-11 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center hover:bg-[#F3E5AB] transition-colors disabled:opacity-40"
          data-testid="chat-send-button"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChatWidget;
